import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const CondolenceEmployeeMaster = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetch('/api/condolence/employee-master', { credentials: 'include' })
      .then(res => {
        if (res.status === 401) { navigate('/condolence'); return null; }
        if (res.status === 403) { navigate('/condolence/menu'); return null; }
        if (!res.ok) { return null; }
        return res.json();
      })
      .then(data => {
        if (data) setEmployees(data.employees || []);
      })
      .catch(() => setError('一覧の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const handleImport = async () => {
    if (!file) {
      setError('CSVファイルを選択してください');
      return;
    }
    setImporting(true);
    setResult(null);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/condolence/employee-master/import', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        setFile(null);
        document.getElementById('csv-file-input').value = '';
        load();
      } else {
        setError(data.error || '取込に失敗しました');
        if (data.errors) setResult({ errors: data.errors });
      }
    } catch {
      setError('通信エラーが発生しました');
    } finally {
      setImporting(false);
    }
  };

  const filtered = employees.filter(e => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      e.store_code.toLowerCase().includes(q) ||
      e.employee_number.toLowerCase().includes(q) ||
      e.name.toLowerCase().includes(q) ||
      (e.category || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="form-page">
      <header className="form-header" style={{ background: '#37474f' }}>
        <button className="back-button" onClick={() => navigate('/condolence/menu')}>← 戻る</button>
        <h1>従業員マスタ取込</h1>
      </header>

      <main className="form-main">
        <div className="section-bar" style={{ background: '#eceff1' }}>
          <span className="icon">👥</span>
          <span className="title" style={{ color: '#37474f' }}>奉行CSVから取込</span>
        </div>

        <div className="form-card">
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
            ※ 奉行から出力したCSVファイルを選択してください。<br />
            ※ 必須ヘッダー: <code>所属番号 / 従業員番号 / 名前 / 区分 / メールアドレス</code><br />
            ※ <strong>メールアドレス欄は空でもOK</strong>（未登録者はURL発行時にSMS/LINE共有用のリンクが表示されます）。<br />
            ※ 取込時は<strong>既存データを全削除して入れ替え</strong>ます（CSVに含まれない人＝退職者は消えます）。<br />
            ※ 文字コードは UTF-8 / Shift-JIS どちらでも可。
          </p>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files[0] || null)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={handleImport}
              disabled={!file || importing}
              style={{
                background: !file || importing ? '#aaa' : '#37474f',
                color: 'white', border: 'none', padding: '10px 20px',
                borderRadius: '6px', cursor: !file || importing ? 'not-allowed' : 'pointer',
                fontSize: '15px',
              }}
            >
              {importing ? '取込中...' : '取込実行'}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {result && result.imported !== undefined && (
            <div style={{
              background: '#e8f5e9', border: '1px solid #66bb6a', borderRadius: '6px',
              padding: '12px', marginBottom: '16px', color: '#2e7d32'
            }}>
              {result.imported} 件取り込みました
              {result.errors && result.errors.length > 0 && (
                <span style={{ color: '#e65100' }}>（{result.errors.length} 件のエラーあり）</span>
              )}
            </div>
          )}

          {result && result.errors && result.errors.length > 0 && (
            <div style={{
              background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '6px',
              padding: '12px', marginBottom: '16px', color: '#e65100', fontSize: '13px',
              maxHeight: '200px', overflowY: 'auto',
            }}>
              <strong>取込エラー:</strong>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="section-bar" style={{ background: '#eceff1', marginTop: '24px' }}>
          <span className="icon">📋</span>
          <span className="title" style={{ color: '#37474f' }}>現在のマスタ（{employees.length} 件）</span>
        </div>

        <div className="form-card">
          <input
            type="text"
            placeholder="絞り込み（店舗コード・名前・従業員番号など）"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              width: '100%', padding: '10px', border: '1px solid #ddd',
              borderRadius: '6px', marginBottom: '12px', fontSize: '14px',
            }}
          />
          {loading ? (
            <p style={{ textAlign: 'center', color: '#666' }}>読み込み中...</p>
          ) : employees.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999' }}>まだ取り込まれていません</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#eceff1' }}>
                    <th style={th}>所属番号</th>
                    <th style={th}>従業員番号</th>
                    <th style={th}>名前</th>
                    <th style={th}>区分</th>
                    <th style={th}>メールアドレス</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(e => (
                    <tr key={e.employee_number}>
                      <td style={td}>{e.store_code}</td>
                      <td style={td}>{e.employee_number}</td>
                      <td style={td}>{e.name}</td>
                      <td style={td}>{e.category}</td>
                      <td style={td}>{e.email || <span style={{ color: '#ccc' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <p style={{ textAlign: 'center', color: '#999', marginTop: '12px' }}>該当なし</p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const th = {
  padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #cfd8dc',
  color: '#37474f', fontWeight: 'bold',
};
const td = {
  padding: '6px 12px', borderBottom: '1px solid #eceff1',
};

export default CondolenceEmployeeMaster;
